/**
 * Card gallery – video autoplay.
 *
 * Mimari (AishaClub / Swiper yaklaşımı):
 *   data-src lazy load + native autoplay
 *   Performans: aynı anda sınırlı decode (mobil 2 / masaüstü 3), tamamen
 *   görünmez slaytlarda src kaldırma, komşu preload sadece “odak” kartta,
 *   bölüm viewport dışındayken autoplay + video durdurma.
 *
 *   Beyaz flash: data-media-loaded yalnızca loadeddata/canplay sonrası; CSS poster fallback.
 *   Autoslide: hover/dokunma ile durmaz (videolu carousel).
 *   Mobil: autoplay süresi tema değerinin ~1.45× ve en az 7 sn.
 */

var isTouchDevice =
  navigator.maxTouchPoints > 0 ||
  window.matchMedia('(pointer: coarse)').matches;

/* edlingerie custom: in-app tarayıcı / düşük performans bayrağı (scripts.liquid) */
function edPerf() {
  return (typeof window !== 'undefined' && window.__edPerf) || {};
}

/* ─── Yardımcılar ─── */

function getPosterButton(dm) {
  return (
    dm.querySelector('[ref="deferredMediaPlayButton"]') ||
    dm.querySelector('.deferred-media__poster-button:not(.video-interaction-hint)')
  );
}

/** Poster decode sonrası skeleton (::before) kapat */
function markGalleryLoaded(img) {
  var gallery = img && img.closest && img.closest('.card-gallery');
  if (gallery) gallery.classList.add('is-loaded');
}

function wirePosterSkeletonForImg(img) {
  if (!img || img.dataset.posterSkeletonWired === 'true') return;
  img.dataset.posterSkeletonWired = 'true';
  if (img.complete && img.naturalWidth > 0) {
    markGalleryLoaded(img);
  } else {
    img.addEventListener(
      'load',
      function () {
        markGalleryLoaded(img);
      },
      { once: true }
    );
    img.addEventListener(
      'error',
      function () {
        markGalleryLoaded(img);
      },
      { once: true }
    );
  }
}

function addSeamlessLoop(video) {
  if (video.dataset.seamlessLoop) return;
  video.dataset.seamlessLoop = 'true';
  video.addEventListener('timeupdate', function () {
    if (video.duration > 0 && video.currentTime > video.duration - 0.15) {
      video.currentTime = 0;
    }
  });
}

/* ─── Kaynakları yükle (data-src → src), oynatma YOK ─── */
function loadVideoSources(video) {
  var loaded = false;
  video.querySelectorAll('source[data-src]').forEach(function (s) {
    if (!s.getAttribute('src')) {
      s.setAttribute('src', s.dataset.src);
      loaded = true;
    }
  });
  if (loaded) video.load();
  return loaded;
}

/* ─── Video etkinleştir (data-src→src → native autoplay) ─── */
function activateVideo(dm) {
  if (!dm) return;

  /* In-app tarayıcı / reduced-motion: video yükleme+decode yapma, poster kalsın */
  if (edPerf().noVideoAutoplay) return;

  var video = dm.querySelector('video.card-autoplay-video');

  if (!video) {
    dm.setAttribute('data-media-loaded', 'true');
    var v = dm.querySelector('video');
    if (v) {
      v.muted = true;
      v.play && v.play().catch(function () {});
    }
    var btn = getPosterButton(dm);
    if (btn) btn.classList.add('deferred-media__playing');
    var gImg = dm.closest('.card-gallery');
    if (gImg) {
      var pi = gImg.querySelector('img.product-media__image');
      if (pi) markGalleryLoaded(pi);
      gImg.classList.add('is-loaded');
    }
    return;
  }

  function markDeferredLoaded() {
    if (!dm.hasAttribute('data-media-loaded')) {
      dm.setAttribute('data-media-loaded', 'true');
    }
  }

  function markVideoPaintReady() {
    video.classList.add('is-ready');
    markDeferredLoaded();
    var gallery = dm.closest('.card-gallery');
    if (gallery) gallery.classList.add('is-loaded');
  }

  var sources = video.querySelectorAll('source[data-src]');
  var notLoaded = Array.from(sources).filter(function (s) {
    return !s.getAttribute('src');
  });

  if (notLoaded.length === 0 && !video.paused) {
    video.classList.add('is-ready');
    return;
  }

  if (notLoaded.length > 0) {
    notLoaded.forEach(function (s) {
      s.setAttribute('src', s.dataset.src);
    });
    video.load();
    addSeamlessLoop(video);

    var paintReadyDone = false;
    function markPaintOnce() {
      if (paintReadyDone) return;
      paintReadyDone = true;
      markVideoPaintReady();
    }
    video.addEventListener('loadeddata', markPaintOnce, { once: true });
    video.addEventListener('canplay', markPaintOnce, { once: true });
    video.addEventListener('playing', markPaintOnce, { once: true });
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(function () {
        if (video.readyState >= 2) markPaintOnce();
      });
    } else {
      setTimeout(function () {
        if (video.readyState >= 2) markPaintOnce();
      }, 0);
    }

    video.addEventListener('playing', function onFirstPlay() {
      video.removeEventListener('playing', onFirstPlay);
      var btn = getPosterButton(dm);
      if (btn) btn.classList.add('deferred-media__playing');
    });

    video.muted = true;
    video.play().catch(function () {});
  } else if (video.paused) {
    if (video.readyState >= 2) {
      markVideoPaintReady();
    }
    video.play().catch(function () {});
    var btn = getPosterButton(dm);
    if (btn) btn.classList.add('deferred-media__playing');
  }
}

/* ─── Video durdur ─── */
function deactivateVideo(dm) {
  if (!dm) return;
  var video = dm.querySelector('video');
  if (video && !video.paused) video.pause();
}

/**
 * Görünüm dışına çıkan kartlar için: src kaldır → ağ/decode belleği serbest.
 * data-src HTML’de durur; tekrar activate ile yüklenir.
 */
function releaseVideoSources(dm) {
  if (!dm) return;
  var video = dm.querySelector('video.card-autoplay-video');
  if (!video) return;
  video.pause();
  video.classList.remove('is-ready');
  dm.removeAttribute('data-media-loaded');
  var changed = false;
  video.querySelectorAll('source').forEach(function (s) {
    if (s.getAttribute('src')) {
      s.removeAttribute('src');
      changed = true;
    }
  });
  if (changed) {
    try {
      video.load();
    } catch (e) {}
  }
  var btn = getPosterButton(dm);
  if (btn) btn.classList.remove('deferred-media__playing');
}

/* ─── Komşu: sadece metadata (hafif prefetch), tam buffer değil ─── */
function preloadAdjacentSlidesLight(currentGallery, allGalleries) {
  var idx = allGalleries.indexOf(currentGallery);
  var len = allGalleries.length;
  if (len < 2) return;
  [-1, 1].forEach(function (offset) {
    var neighborIdx = (idx + offset + len) % len;
    var neighbor = allGalleries[neighborIdx];
    if (!neighbor || neighbor === currentGallery) return;
    var dm = neighbor.querySelector('deferred-media');
    if (!dm) return;
    var video = dm.querySelector('video.card-autoplay-video');
    if (!video) return;
    var sources = video.querySelectorAll('source[data-src]');
    var needsLoad = false;
    sources.forEach(function (s) {
      if (!s.getAttribute('src')) {
        s.setAttribute('src', s.dataset.src);
        needsLoad = true;
      }
    });
    if (needsLoad) {
      video.preload = 'metadata';
      video.load();
    }
  });
}

/** Eski isim – carousel’da sadece tek “odak” karttan çağrılır */
function preloadAdjacentSlides(currentGallery, allGalleries) {
  preloadAdjacentSlidesLight(currentGallery, allGalleries);
}

function getMaxPlayingVideos() {
  /* Yalnızca in-app / düşük performansta tavanı override et.
     Normal web + normal mobil orijinal mantıkta kalsın (davranış değişmesin). */
  var p = edPerf();
  if (p.lowPerf && typeof p.maxVideos === 'number' && p.maxVideos > 0) {
    return p.maxVideos;
  }
  if (typeof navigator !== 'undefined' && navigator.connection) {
    var c = navigator.connection;
    if (c.saveData) return 1;
  }
  var narrow =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 749px)').matches;
  if (isTouchDevice || narrow) return 2;
  return 3;
}

/**
 * Scrollport (scroller) ile slayt kutusunun kesişiminden görünürlük oranı.
 * IntersectionObserver yerine bunu kullanıyoruz; Safari/theme ilk frame’de 0 verebiliyor.
 */
function mergeGeometricSlideRatios(scroller, slideElements, slideRatio) {
  var sb = scroller.getBoundingClientRect();
  slideElements.forEach(function (slide) {
    var r = slide.getBoundingClientRect();
    var left = Math.max(r.left, sb.left);
    var right = Math.min(r.right, sb.right);
    var top = Math.max(r.top, sb.top);
    var bottom = Math.min(r.bottom, sb.bottom);
    var w = right - left;
    var h = bottom - top;
    var g = 0;
    if (w > 0 && h > 0) {
      var vis = w * h;
      var area = Math.max(r.width * r.height, 1);
      var gArea = vis / area;
      var gW = w / Math.max(r.width, 1);
      var gH = h / Math.max(r.height, 1);
      g = Math.max(gArea, gW, gH);
    }
    slideRatio.set(slide, g);
  });
}

/* ═══════════════════════════════════════════════════════
   KLON TABANLI SONSUZ CAROUSEL (Swiper loop mantığı)
   Kartlar: ...6 7 8 [1 2 3 4] 5 6 7 8 1 2 3...
   Gerçek slide'ların öncesine ve sonrasına klonlar eklenir.
   Scroll klon bölgesine girince sessizce gerçek konuma atlar.
   ═══════════════════════════════════════════════════════ */

function initInfiniteCarousel(slideshow) {
  if (slideshow.dataset.videoCarouselInit) return;
  slideshow.dataset.videoCarouselInit = 'true';

  var scroller = slideshow.querySelector('slideshow-slides');
  if (!scroller) return;

  var realSlides = Array.from(scroller.querySelectorAll(':scope > slideshow-slide'));
  if (realSlides.length < 2) return;

  /* ── Ölçüler (klonlardan sonra layout yeniden hesaplanır) ── */
  var gap = parseInt(getComputedStyle(scroller).gap, 10) || 0;
  var slideW = realSlides[0].offsetWidth + gap;
  var visibleCount = Math.max(1, Math.round(scroller.offsetWidth / Math.max(slideW - gap, 1)));
  var cloneCount = visibleCount + 1;
  var realCount = realSlides.length;
  var narrowMobile =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 749px)').matches;
  var baseAutoplaySec = parseInt(slideshow.dataset.autoplaySpeed, 10) || 4;
  var autoplayMs = baseAutoplaySec * 1000;
  /* Mobil: kaydırma biraz daha yavaş (tema süresi ×1.45, en az 7 sn) */
  if (narrowMobile) {
    autoplayMs = Math.max(Math.round(autoplayMs * 1.45), 7000);
  }

  /* ── Dots referansı ── */
  var dots = slideshow.querySelectorAll('.slideshow-controls__dots button[ref="dots[]"]');

  /* ── Klonları oluştur (resource-list__slide + --slide-width aynı kalsın) ── */
  function makeClone(slide) {
    var el = document.createElement('div');
    el.className = (slide.className || '') + ' video-loop-clone';
    el.innerHTML = slide.innerHTML;
    el.setAttribute('aria-hidden', 'true');
    /* Videoyu SİLME: sağdan devam eden “ilk” kartlar klon; AishaClub gibi hepsi oynasın */
    el.querySelectorAll('[id]').forEach(function (n) {
      n.removeAttribute('id');
    });
    return el;
  }

  function getDeckSlides() {
    return Array.from(scroller.children).filter(function (node) {
      return node.matches && node.matches('slideshow-slide, .video-loop-clone');
    });
  }

  /* Sona klon: ilk N slide'ın kopyası */
  for (var i = 0; i < cloneCount; i++) {
    scroller.appendChild(makeClone(realSlides[i % realCount]));
  }
  /* Başa klon: son N slide'ın kopyası */
  var frag = document.createDocumentFragment();
  for (var j = realCount - cloneCount; j < realCount; j++) {
    frag.appendChild(makeClone(realSlides[(j + realCount) % realCount]));
  }
  scroller.insertBefore(frag, scroller.firstChild);

  /* Klonlardan sonra gerçek genişliği tekrar ölç (flex + snap hizası) */
  slideW = realSlides[0].offsetWidth + gap;

  /* ── Başlangıç scroll pozisyonu: klonların sonrasında gerçek ilk slide ── */
  var offsetLeft = cloneCount * slideW;
  scroller.style.scrollBehavior = 'auto';
  scroller.scrollLeft = offsetLeft;
  scroller.style.scrollBehavior = '';

  /**
   * Sıra: data-carousel-product-index (Liquid); clone + gerçek slide.
   * İlk N benzersiz ürün: eager; ilk M: fetchPriority high (mobil tavan düşük).
   */
  function promoteCarouselPosters() {
    var highCap = narrowMobile ? 3 : 10;
    var eagerCap = narrowMobile ? 5 : 16;
    var hi = 0;
    var ej = 0;
    var seenProduct = new Set();
    var slides = Array.from(
      scroller.querySelectorAll(':scope > slideshow-slide, :scope > .video-loop-clone')
    );
    slides.sort(function (a, b) {
      var na = a.querySelector('[data-carousel-product-index]');
      var nb = b.querySelector('[data-carousel-product-index]');
      var ia = na ? parseInt(na.getAttribute('data-carousel-product-index'), 10) || 999 : 999;
      var ib = nb ? parseInt(nb.getAttribute('data-carousel-product-index'), 10) || 999 : 999;
      return ia - ib;
    });
    slides.forEach(function (slide) {
      var img = slide.querySelector('img.product-media__image');
      if (!img) return;
      var gal = img.closest('.card-gallery');
      var pid = gal ? gal.getAttribute('data-product-id') : '';
      if (pid && seenProduct.has(pid)) {
        wirePosterSkeletonForImg(img);
        return;
      }
      if (pid) seenProduct.add(pid);
      if (ej < eagerCap) {
        img.loading = 'eager';
        if ('fetchPriority' in img) {
          img.fetchPriority = hi < highCap ? 'high' : 'low';
          if (hi < highCap) hi++;
        }
        ej++;
      }
      wirePosterSkeletonForImg(img);
    });
  }
  promoteCarouselPosters();

  /* ── Scroll-snap geçici kapatma yardımcısı ── */
  function disableSnap() { scroller.style.scrollSnapType = 'none'; }
  function enableSnap() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        scroller.style.scrollSnapType = '';
      });
    });
  }

  /* ── Hangi gerçek slide index'indeyiz (0-based) ── */
  function getCurrentRealIndex() {
    var raw = Math.round(scroller.scrollLeft / slideW) - cloneCount;
    return ((raw % realCount) + realCount) % realCount;
  }

  /* ── Tüm gerçek kart galerileri (preload + senkron için) ── */
  var galleries = [];
  realSlides.forEach(function (s) {
    var g = s.querySelector('.card-gallery[data-first-media-video="true"]');
    if (g) galleries.push(g);
  });

  /* ── Dots güncelle ── */
  function updateDots() {
    var idx = getCurrentRealIndex();
    dots.forEach(function (d, i) {
      d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });
  }

  /* ── Görünürlük: geometri + gerçek/klon aynı ürün → tek DOM’da oynat (çift decode yok) ── */
  var MIN_VISIBLE_RATIO = 0.05;
  var RELEASE_RATIO = 0.02;
  var rawCap = getMaxPlayingVideos();
  var maxPlaying;
  if (narrowMobile) {
    maxPlaying = Math.min(rawCap, Math.max(1, visibleCount));
  } else {
    maxPlaying =
      rawCap <= 1 ? rawCap : Math.min(8, Math.max(rawCap, visibleCount));
  }
  var sectionInView = true;
  var scrollRaf = 0;

  function applyVideoAndAria() {
    if (!sectionInView) return;

    var deckSlides = getDeckSlides();
    var ratios = new Map();
    mergeGeometricSlideRatios(scroller, deckSlides, ratios);

    var bestByProduct = new Map();
    for (var di = 0; di < deckSlides.length; di++) {
      var el = deckSlides[di];
      var gallery = el.querySelector('.card-gallery[data-first-media-video="true"]');
      if (!gallery) continue;
      var dm = gallery.querySelector('deferred-media');
      if (!dm) continue;
      var pid = gallery.getAttribute('data-product-id');
      if (!pid) pid = '__idx_' + di;
      var rv = ratios.get(el) || 0;
      var cur = bestByProduct.get(pid);
      var isReal = el.matches && el.matches('slideshow-slide');
      if (!cur || rv > cur.r) {
        bestByProduct.set(pid, { el: el, r: rv, gallery: gallery, dm: dm });
      } else if (cur && Math.abs(rv - cur.r) < 0.005) {
        var curReal = cur.el.matches && cur.el.matches('slideshow-slide');
        if (isReal && !curReal) {
          bestByProduct.set(pid, { el: el, r: rv, gallery: gallery, dm: dm });
        }
      }
    }

    var candidates = [];
    bestByProduct.forEach(function (v) {
      if (v.r >= MIN_VISIBLE_RATIO) candidates.push(v);
    });
    candidates.sort(function (a, b) {
      return b.r - a.r;
    });

    var active = [];
    for (var pi = 0; pi < candidates.length && active.length < maxPlaying; pi++) {
      active.push(candidates[pi]);
    }
    var activeElSet = new Set(active.map(function (x) {
      return x.el;
    }));

    var focusGallery = active.length ? active[0].gallery : null;

    for (var dj = 0; dj < deckSlides.length; dj++) {
      var slideEl = deckSlides[dj];
      var gal = slideEl.querySelector('.card-gallery[data-first-media-video="true"]');
      if (!gal) continue;
      var deferred = gal.querySelector('deferred-media');
      if (!deferred) continue;
      var r = ratios.get(slideEl) || 0;
      var pkey = gal.getAttribute('data-product-id');
      if (!pkey) pkey = '__idx_' + dj;
      var winner = bestByProduct.get(pkey);

      if (activeElSet.has(slideEl)) {
        slideEl.setAttribute('aria-hidden', 'false');
        activateVideo(deferred);
      } else if (winner && winner.el !== slideEl) {
        slideEl.setAttribute('aria-hidden', 'true');
        releaseVideoSources(deferred);
      } else if (r < RELEASE_RATIO) {
        slideEl.setAttribute('aria-hidden', 'true');
        releaseVideoSources(deferred);
      } else {
        slideEl.setAttribute('aria-hidden', 'true');
        deactivateVideo(deferred);
      }
    }

    if (!narrowMobile && focusGallery && galleries.length) {
      preloadAdjacentSlidesLight(focusGallery, galleries);
    }
  }

  var ioRaf = 0;
  /* IO sadece yeniden ölçüm zamanlar — oranları Safari/theme bazen yanlış veriyor */
  var videoIo = new IntersectionObserver(
    function () {
      cancelAnimationFrame(ioRaf);
      ioRaf = requestAnimationFrame(function () {
        applyVideoAndAria();
      });
    },
    {
      root: scroller,
      threshold: [0, 0.05, 0.1, 0.25, 0.5, 0.75, 1],
      rootMargin: '0px',
    }
  );

  getDeckSlides().forEach(function (slide) {
    videoIo.observe(slide);
  });

  function flushIoAndApply() {
    if (videoIo.takeRecords) videoIo.takeRecords();
    applyVideoAndAria();
  }

  /* ── Sınır kontrolü: klon bölgesindeyse sessizce atla ── */
  var jumping = false;
  function checkBounds() {
    if (jumping) return;
    var minReal = cloneCount * slideW;
    var maxReal = (cloneCount + realCount - 1) * slideW;
    var sl = scroller.scrollLeft;
    var needsJump = false;
    var target = sl;

    if (sl < minReal - slideW * 0.5) {
      target = sl + realCount * slideW;
      needsJump = true;
    } else if (sl > maxReal + slideW * 0.5) {
      target = sl - realCount * slideW;
      needsJump = true;
    }

    if (needsJump) {
      jumping = true;
      disableSnap();
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollLeft = target;
      scroller.style.scrollBehavior = '';
      enableSnap();
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          jumping = false;
          updateDots();
          flushIoAndApply();
        });
      });
      return;
    }

    updateDots();
    flushIoAndApply();
  }

  /* ── Scroll: momentum sırasında video/aria (AishaClub benzeri akıcılık) ── */
  var scrollTimer;
  scroller.addEventListener(
    'scroll',
    function () {
      if (!scrollRaf) {
        scrollRaf = requestAnimationFrame(function () {
          scrollRaf = 0;
          if (sectionInView) applyVideoAndAria();
        });
      }
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        checkBounds();
      }, 80);
    },
    { passive: true }
  );

  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      flushIoAndApply();
      setTimeout(flushIoAndApply, 120);
    });
  });

  scroller.addEventListener('scrollend', function () {
    checkBounds();
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && slideshow.isConnected && sectionInView) flushIoAndApply();
  });

  /* ── Autoplay timer ── */
  var autoTimer;
  function startAutoplay() {
    clearInterval(autoTimer);
    if (!sectionInView) return;
    autoTimer = setInterval(function () {
      if (!sectionInView || slideshow.matches(':hover') || document.hidden) return;
      scroller.scrollTo({ left: scroller.scrollLeft + slideW, behavior: 'smooth' });
    }, autoplayMs);
  }

  function syncRoughSectionVisibility() {
    try {
      var r = slideshow.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var margin = 120;
      sectionInView = r.bottom > -margin && r.top < vh + margin;
    } catch (err) {
      sectionInView = true;
    }
  }

  var sectionViewIo = new IntersectionObserver(
    function (entries) {
      var ent = entries[0];
      if (!ent) return;
      var vis = ent.isIntersecting;
      if (vis === sectionInView) return;
      sectionInView = vis;
      if (!vis) {
        clearInterval(autoTimer);
        getDeckSlides().forEach(function (slide) {
          var g = slide.querySelector('.card-gallery[data-first-media-video="true"]');
          if (!g) return;
          var dm = g.querySelector('deferred-media');
          if (!dm) return;
          releaseVideoSources(dm);
          slide.setAttribute('aria-hidden', 'true');
        });
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (videoIo.takeRecords) videoIo.takeRecords();
            flushIoAndApply();
          });
        });
        startAutoplay();
      }
    },
    { root: null, threshold: 0, rootMargin: '120px 0px' }
  );
  sectionViewIo.observe(slideshow);

  syncRoughSectionVisibility();
  startAutoplay();

  /* Videolu carousel: hover / dokunma autoslide’ı durdurmasın */

  /* ── Arrowları devral ── */
  var prevBtn = slideshow.querySelector('[ref="previous"], .slideshow-arrow--previous');
  var nextBtn = slideshow.querySelector('[ref="next"], .slideshow-arrow--next');

  if (nextBtn) {
    nextBtn.disabled = false;
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      clearInterval(autoTimer);
      scroller.scrollTo({ left: scroller.scrollLeft + slideW, behavior: 'smooth' });
      setTimeout(startAutoplay, 3000);
    }, true);
  }
  if (prevBtn) {
    prevBtn.disabled = false;
    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      clearInterval(autoTimer);
      scroller.scrollTo({ left: scroller.scrollLeft - slideW, behavior: 'smooth' });
      setTimeout(startAutoplay, 3000);
    }, true);
  }

  /* Tema slideshow.js bazen next/prev disabled yapıyor — zorla açık tut */
  function enforceArrowsEnabled() {
    if (nextBtn) nextBtn.disabled = false;
    if (prevBtn) prevBtn.disabled = false;
  }
  if (nextBtn) {
    var moNext = new MutationObserver(enforceArrowsEnabled);
    moNext.observe(nextBtn, { attributes: true, attributeFilter: ['disabled'] });
  }
  if (prevBtn) {
    var moPrev = new MutationObserver(enforceArrowsEnabled);
    moPrev.observe(prevBtn, { attributes: true, attributeFilter: ['disabled'] });
  }
  enforceArrowsEnabled();

  /* ── Dots tıklamasını devral ── */
  dots.forEach(function (dot, i) {
    dot.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      clearInterval(autoTimer);
      var target = (cloneCount + i) * slideW;
      scroller.scrollTo({ left: target, behavior: 'smooth' });
      setTimeout(startAutoplay, 3000);
    }, true);
  });

  /* İlk dots durumu */
  updateDots();
}

/* ─── Eski IO tabanlı carousel (video carousel olmayan) ─── */
function initCarouselAutoplay(slideshow) {
  if (slideshow.dataset.videoCarouselInit) return;
  slideshow.dataset.videoCarouselInit = 'true';

  var scroller = slideshow.querySelector('slideshow-slides');
  if (!scroller) return;

  var galleries = Array.from(
    slideshow.querySelectorAll('.card-gallery[data-first-media-video="true"]')
  );
  if (!galleries.length) return;

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var dm = entry.target.querySelector('deferred-media');
        if (!dm) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          activateVideo(dm);
          preloadAdjacentSlidesLight(entry.target, galleries);
        } else {
          deactivateVideo(dm);
          if (!entry.isIntersecting) releaseVideoSources(dm);
        }
      });
    },
    { root: scroller, threshold: 0.5, rootMargin: '0px' }
  );

  galleries.forEach(function (g) {
    observer.observe(g);
    var im = g.querySelector('img.product-media__image');
    if (im) wirePosterSkeletonForImg(im);
  });
}

/* ─── GRID mobile: IO viewport ─── */
function initGridAutoplay(gallery) {
  if (gallery.dataset.videoGridInit) return;
  gallery.dataset.videoGridInit = 'true';
  var dm = gallery.querySelector('deferred-media');
  if (!dm) return;
  var imgG = gallery.querySelector('img.product-media__image');
  if (imgG) wirePosterSkeletonForImg(imgG);
  var triggered = false;
  var observer = new IntersectionObserver(
    function (entries) {
      if (triggered) return;
      if (entries[0] && entries[0].isIntersecting) {
        triggered = true;
        observer.disconnect();
        activateVideo(dm);
      }
    },
    { rootMargin: '100px 0px', threshold: 0 }
  );
  observer.observe(gallery);
}

/* ─── GRID desktop: hover ─── */
function initDesktopHover(gallery, card) {
  if (gallery.dataset.videoDesktopInit) return;
  gallery.dataset.videoDesktopInit = 'true';
  var imgD = gallery.querySelector('img.product-media__image');
  if (imgD) wirePosterSkeletonForImg(imgD);
  card.addEventListener('mouseenter', function () {
    activateVideo(gallery.querySelector('deferred-media'));
  });
  card.addEventListener('mouseleave', function () {
    var dm = gallery.querySelector('deferred-media');
    if (!dm) return;
    deactivateVideo(dm);
    var btn = getPosterButton(dm);
    if (btn) btn.classList.remove('deferred-media__playing');
    var video = dm.querySelector('video');
    if (video) video.currentTime = 0;
  });
}

/* ─── Section videolar ─── */
function initSectionVideoAutoplay() {
  document.querySelectorAll('deferred-media[autoplay]').forEach(function (dm) {
    if (dm.closest('.card-gallery')) return;
    if (dm.dataset.sectionAutoplayInit) return;
    dm.dataset.sectionAutoplayInit = 'true';
    var observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) activateVideo(dm);
        else deactivateVideo(dm);
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(dm);
  });
}

/**
 * Görselleri hafiflet: tema `sizes="auto, ..."` kullanıyor; eski iOS Safari
 * (Instagram WebView) `auto`'yu tanımadığından çok büyük srcset adayını (3840px)
 * seçip 4G'de görselleri "hiç gelmedi" gibi çok yavaş yüklüyor.
 * Burada sizes'ı gerçek render genişliğine sabitliyoruz → tarayıcı en küçük
 * yeterli görseli seçer. Ayrıca ilk ekrandaki görselleri eager+high yapıyoruz.
 */
function liteOptimizeImages(root) {
  var scope = root && root.querySelectorAll ? root : document;
  var vw = window.innerWidth || document.documentElement.clientWidth || 400;
  var vh = window.innerHeight || document.documentElement.clientHeight || 800;

  scope.querySelectorAll('img[sizes]').forEach(function (img) {
    if (img.dataset.liteSized === '1') return;
    var s = img.getAttribute('sizes') || '';
    if (s.indexOf('auto') === -1) return; // yalnızca auto olanları düzelt
    img.dataset.liteSized = '1';

    var rect = img.getBoundingClientRect();
    // sizes CSS pikseli ister; DPR'ı tarayıcı kendi uygular (çift sayma!)
    var w = rect.width || img.clientWidth || 0;
    if (!w) w = vw; // görünür değilse viewport genişliğini üst sınır al
    w = Math.min(Math.round(w), vw);
    if (w > 0) img.setAttribute('sizes', w + 'px');

    // İlk ekranda görünenler: hemen ve yüksek öncelikle yüklensin
    var inView = rect.top < vh && rect.bottom > 0 && rect.left < vw && rect.right > 0;
    if (inView) {
      try {
        img.loading = 'eager';
        if ('fetchPriority' in img) img.fetchPriority = 'high';
      } catch (e) {
        /* ignore */
      }
    }
  });
}

/**
 * LITE MOD (in-app tarayıcı / reduced-motion):
 * Hiç klon, hiç IntersectionObserver, hiç scroll handler kurma. Bunlar eski
 * telefonlarda WebView'i belleksizlikten çökertip reload attırıyordu.
 * Poster iskeletlerini temizler, görselleri hafifletir; videolar zaten
 * noVideoAutoplay guard'ı ile oynamaz, native scroll çalışır.
 */
function initLiteCardGalleries() {
  document.querySelectorAll('.card-gallery').forEach(function (g) {
    var im = g.querySelector('img.product-media__image');
    if (im) wirePosterSkeletonForImg(im);
    g.classList.add('is-loaded');
  });
  liteOptimizeImages(document);
  // Geç render olan görseller için kısa bir tekrar
  setTimeout(function () {
    liteOptimizeImages(document);
  }, 300);
}

/* ─── Ana init ─── */
function initCardGalleryVideoHover() {
  var p = edPerf();

  // In-app / reduced-motion: tamamen hafif yol (observer/klon yok).
  if (p.noVideoAutoplay) {
    initLiteCardGalleries();
    return;
  }

  var lowPerf = !!p.lowPerf;

  // Video kartlı carousel: normalde klon tabanlı sonsuz döngü.
  // In-app tarayıcı / düşük performansta klon + MutationObserver + autoplay
  // timer yükünü atlayıp hafif IO tabanlı yola düşüyoruz (native scroll kalır).
  document.querySelectorAll('slideshow-component[data-video-carousel]').forEach(function (slideshow) {
    if (lowPerf) {
      initCarouselAutoplay(slideshow);
    } else {
      initInfiniteCarousel(slideshow);
    }
  });

  // Diğer carousel'lar: IO tabanlı video autoplay
  document.querySelectorAll('slideshow-component').forEach(function (slideshow) {
    if (slideshow.hasAttribute('data-video-carousel')) return;
    if (slideshow.querySelector('.card-gallery[data-first-media-video="true"]')) {
      initCarouselAutoplay(slideshow);
    }
  });

  // Carousel dışı kartlar — tüm cihazlarda viewport IO autoplay
  document.querySelectorAll('.card-gallery[data-first-media-video="true"]').forEach(function (gallery) {
    if (gallery.closest('slideshow-component')) return;
    initGridAutoplay(gallery);
  });

  initSectionVideoAutoplay();

  document.querySelectorAll('[data-video-card-style="true"] .resource-list__carousel .card-gallery').forEach(function (g) {
    var im = g.querySelector('img.product-media__image');
    if (im) wirePosterSkeletonForImg(im);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCardGalleryVideoHover);
} else {
  initCardGalleryVideoHover();
}

document.addEventListener('shopify:section:load', function (e) {
  e.target.querySelectorAll('[data-video-carousel-init],[data-video-grid-init],[data-video-desktop-init]')
    .forEach(function (el) {
      delete el.dataset.videoCarouselInit;
      delete el.dataset.videoGridInit;
      delete el.dataset.videoDesktopInit;
    });
  initCardGalleryVideoHover();
});
